import {makeAutoObservable, runInAction} from "mobx";
import {rootContainer, TYPE} from "../../../../../services/container/root";
import IframeService, {LARAVEL_TYPE} from "../../../../../services/iframe-service/iframe-service";
import UserMasterListService from "../../../../../services/api/user-master-list-service";
import {ADMIN_ROLE_NAMES} from "../../../stores/user-master-list-store";
import { isSet } from '../../../../../models/model-types-methods';

type SwitchShopRequestMessage = {
    shopId?: number|null;
}

class ShopSwitchStore {
    private _showReasonModal: boolean = false;
    private _reasonPromiseResolve: ((reason: string) => void) | null = null;
    private _reasonPromiseReject: ((error: Error) => void) | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get _userMasterListService(): UserMasterListService { return rootContainer.get<UserMasterListService>(TYPE.UserMasterListService); }
    private get _iframeService(): IframeService { return rootContainer.get<IframeService>(TYPE.IframService); }

    private async _findEditorRoleId(): Promise<number|null> {
        const roles = await this._userMasterListService.fetchLoginRoles();
        const editorRole = roles.find(role => role.name.toLowerCase() === ADMIN_ROLE_NAMES.EDITOR);

        return editorRole?.id ?? null;
    }

    private async _handleLoginRequestFromLaravelDropdown(message: SwitchShopRequestMessage) {
        try {
            const shopId = message?.shopId;
            if (!isSet(shopId)) {
                return;
            }

            const editorRoleId = await this._findEditorRoleId();

            if (!isSet(editorRoleId)) {
                return;
            }

            const reason = await this.askForReason();

            if (!reason) {
                return;
            }

            await this._userMasterListService.login(shopId, editorRoleId, reason);
            this._iframeService.refreshParentFrame();
        }
        catch (error) {
            console.error('[ShopSwitchStore] Error switching shop from Laravel Dropdown:', error);
        }
    }

    private _initAskForReasonPromise(): Promise<string|null> {
        return new Promise((resolve, reject) => {
            this._reasonPromiseResolve = resolve;
            this._reasonPromiseReject = reject;
        });
    }

    get showReasonModal() {
        return this._showReasonModal;
    }

    public subscribeToParentShopSwitchEvent(): () => void {
        return this._iframeService.subscribeToParentEvent(
            LARAVEL_TYPE.REQUEST_SHOP_SWITCH_REASON,
                message => this._handleLoginRequestFromLaravelDropdown(message as SwitchShopRequestMessage));
    }

    public resolveWithReason(reason: string) {
        if (isSet(this._reasonPromiseResolve) && isSet(reason)) {
            this._reasonPromiseResolve(reason);
        }
    }

    public closeReasonModal() {
        this._showReasonModal = false;

        if (isSet(this._reasonPromiseReject)) {
            this._reasonPromiseReject(new Error('The Reason Modal was closed'));
            this._reasonPromiseReject = null;
        }

        if (isSet(this._reasonPromiseResolve)) {
            this._reasonPromiseResolve = null;
        }
    }

    public async askForReason(): Promise<string|null> {
        if (isSet(this._reasonPromiseResolve) && isSet(this._reasonPromiseReject)) {
            console.error('[ShopSwitchStore] Cannot ask for a login reason multiple times simultaneously');
            return null;
        }

        this._showReasonModal = true;

        try {
            return await this._initAskForReasonPromise();
        }
        catch (e) {
            return null;
        }
        finally {
            runInAction(() => {
                this._showReasonModal = false;
            });
        }
    }
}

export default new ShopSwitchStore();